<script setup lang="ts">
import CardComponent from "@/components/CardComponent.vue";
import CardDisplay from "@/components/CardDisplay.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCheckbox from "@/components/StyledCheckbox.vue";
import StyledInput from "@/components/StyledInput.vue";
import useCompanyBillingInformation from "@/modules/companies/composables/useCompanyBillingInformation.ts";
import useCompanyOpenPayCard from "@/modules/companies/composables/useCompanyOpenPayCard.ts";
import { Company } from "@/modules/companies/interfaces/company.ts";
import { useErrorCodeOpenPay } from "@/modules/hotels/composables/useErrorCodeOpenPay.ts";
import useUserOpenPayCard from "@/modules/users/composables/useUserOpenPayCard.ts";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useToast } from "vue-toastification";

const openPay = window.OpenPay;

interface Props {
  company: Company;
}

openPay.setId(import.meta.env.VITE_OPENPAY_ID);
openPay.setApiKey(import.meta.env.VITE_OPENPAY_KEY);
openPay.setSandboxMode(import.meta.env.VITE_OPENPAY_PRODUCTION_MODE !== 'true');


const props = defineProps<Props>();
const toast = useToast();

const queryClient = useQueryClient();
const { getErrorCode } = useErrorCodeOpenPay();
const deviceDataId = openPay.deviceData.setup("formId");

const { cards, createCardAsync, createCardLoading, isLoading, refetch } =
  useCompanyOpenPayCard({
    params: {
      companyId: props.company.id,
    },
    immediate: true,
  });

const { data: billingData } = useCompanyBillingInformation({
  params: { id: props.company.id },
  immediate: true,
});

const hasBillingInfo = computed(() => !!billingData.value);
const canAddCard = computed(() => !!props.company.email && hasBillingInfo.value);

const modalFormCard = ref(false);

const actionFormCard = async () => {
  modalFormCard.value = !modalFormCard.value;
};

const rules = {
  card_number: {
    required: helpers.withMessage(
      "El número de tarjeta es requerido",
      required,
    ),
    requiredIf: helpers.withMessage("El número de tarjeta es inválido", () => {
      return openPay.card.validateCardNumber(
        formModels.card_number.replace(/\s/g, ""),
      );
    }),
  },
  cvv2: {
    required: helpers.withMessage(
      "El código de seguridad es requerido",
      required,
    ),
    requiredIf: helpers.withMessage(
      "El código de seguridad es inválido",
      () => {
        return openPay.card.validateCVC(formModels.cvv2);
      },
    ),
  },
  expiry: {
    required: helpers.withMessage(
      "La fecha de expiración es requerida",
      required,
    ),
    requiredIf: helpers.withMessage(
      "La fecha de expiración es inválida",
      () => {
        return openPay.card.validateExpiry(
          formModels.expiry.expiration_month,
          formModels.expiry.expiration_year,
        );
      },
    ),
  },
  holder_name: {
    required: helpers.withMessage(
      "El nombre del titular es requerido",
      required,
    ),
    requiredIf: helpers.withMessage("El nombre del titular es inválido", () => {
      return !new RegExp(/[^a-zA-ZáéíóúñÁÉÍÓÚÑ\s]/g).test(
        formModels.holder_name,
      );
    }),
  },
};

const formModels = reactive({
  holder_name: props.company.name,
  card_number: "",
  cvv2: "",
  expiry: {
    expiration_year: "",
    expiration_month: "",
  },
  alias: "",
});

const v$ = useVuelidate(rules, formModels);
const cvvLength = computed(() => {
  return formModels.card_number.startsWith("3") ? 4 : 3;
});

const numberLength = computed(() => {
  return formModels.card_number.startsWith("3") ? 15 : 16;
});

const isBackVisible = ref(false);

const actionBack = () => {
  isBackVisible.value = !isBackVisible.value;
};

const onSuccess = async (response: any) => {
  if (deviceDataId && response) {
    try {
      await createCardAsync({
        params: {
          cardId: response.data.id,
          deviceId: deviceDataId,
          companyId: props.company.id.toString(),
        },
        config: {
          onSuccessToast: "Tarjeta guardada correctamente",
          onErrorToast: "Hubo un error al guardar la tarjeta, intente de nuevo",
          raiseToast: true,
        },
      });

      await queryClient.invalidateQueries(["companyCards", props.company.id]);
      actionFormCard();

      formModels.card_number = "";
      formModels.expiry.expiration_month = "";
      formModels.expiry.expiration_year = "";
      formModels.cvv2 = "";
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(getErrorCode(error.response?.data?.error?.code));
        return;
      }
      toast.error(error.response?.data?.message);
    }
  }
};

const onError = async (response: any) => {
  if (response.data.error_code) {
    toast.error(getErrorCode(response?.data?.error_code));
    return;
  }
  toast.error(response?.data?.description);
};

const handleSave = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  if (!v$.value.$invalid) {
    openPay.token.create(
      {
        ...formModels,
        card_number: formModels.card_number.replace(/\s/g, ""),
        expiration_month: formModels.expiry.expiration_month,
        expiration_year: formModels.expiry.expiration_year,
      },
      onSuccess,
      onError,
    );
  }
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex items-center mb-3 justify-between">
      <div>
        <h2 class="text-xl font-medium">
          Mis tarjetas {{ cards.length ?? 0 }}/6
        </h2>
        <div v-if="props.company.email" class="flex items-center gap-1 mt-1">
          <span class="material-symbols-outlined text-gray-400 text-sm">mail</span>
          <span class="text-xs text-gray-500">{{ props.company.email }}</span>
        </div>
      </div>

      <button
        class="text-blue-500 bg-white hover:text-blue-800 flex"
        @click="actionFormCard"
        v-if="cards.length < 6 && canAddCard"
      >
        <span class="material-symbols-outlined"> add </span>
        <p>Agregar tarjeta</p>
      </button>
    </div>

    <!-- Warning: no email + no billing -->
    <div
      v-if="!canAddCard"
      class="flex items-start gap-3 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg"
    >
      <span class="material-symbols-outlined text-amber-500 text-base flex-shrink-0 mt-0.5">warning</span>
      <p class="text-sm text-amber-800">
        Esta empresa no tiene correo ni información de facturación registrada. Es necesario contar con al menos uno para agregar tarjetas.
      </p>
    </div>

    <!-- Card form -->
    <ModalComponent
      v-if="canAddCard"
      :modelValue="modalFormCard"
      :onUpdate:modelValue="actionFormCard"
    >
      <template #modalBody>
        <div class="p-5">
          <h2 class="font-bold text-xl">Crear tarjeta</h2>
          <div class="flex items-center w-full justify-center p-10">
            <CardDisplay
              :userName="formModels.holder_name"
              :form="{
                expDate: {
                  month: Number(formModels.expiry.expiration_month),
                  year: Number(formModels.expiry.expiration_year),
                },
              }"
              :cvv="formModels.cvv2"
              :number="formModels.card_number"
              :isBackVisible="isBackVisible"
              @toggleBackCard="actionBack"
              :isForm="true"
            />
          </div>
          <!-- data-screenshot-exclude: dato de tarjeta fuera de la captura del widget de bugs (ver useScreenCapture.ts). -->
          <form @submit.prevent="handleSave()" data-screenshot-exclude>
            <StyledInput
              v-model:modelValue="formModels.card_number"
              label="Número de tarjeta"
              :isRequired="true"
              :maxLength="numberLength"
            />

            <div class="flex gap-2 mt-2">
              <div class="flex gap-2">
                <StyledInput
                  v-model:modelValue="formModels.expiry.expiration_month"
                  label="Mes"
                  :isRequired="true"
                  :maxLength="2"
                />

                <StyledInput
                  v-model:modelValue="formModels.expiry.expiration_year"
                  label="Año"
                  :isRequired="true"
                  :maxLength="2"
                />
              </div>

              <div class="w-1/2">
                <StyledInput
                  v-model:modelValue="formModels.cvv2"
                  label="CVV"
                  :isRequired="true"
                  :maxLength="cvvLength"
                  @focus-in="actionBack"
                  @focus-out="actionBack"
                />
              </div>
            </div>

            <StyledButton
              label="Guardar tarjeta"
              class="bg-blue-600 ml-auto text-white mt-3"
              :loading="createCardLoading"
            />
          </form>
        </div>
      </template>
    </ModalComponent>

    <!-- Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div
        v-for="(card, i) in cards"
        :key="i"
      >
        <CardComponent
          :cardId="card.id"
          :brand="card.brand"
          :card-number="card.cardNumber"
          :expiration-month="card.expirationMonth"
          :expiration-year="card.expirationYear"
          :holder-name="card.holderName"
          ownership="company"
          :query-key="['companyCards', props.company.id]"
        />
      </div>
    </div>
  </ShadowBox>

  <LoadingModal :when="isLoading" />
</template>

<style scoped></style>
